/**
 * CR157: Scheduler Debug Header
 * Shows source, schedule_run_id, and run_until for scheduler-created batch runs
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Calendar, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getRunUntilConfig } from "@/lib/scheduler/runUntilConfig";
import type { ApprovalCheckpoint } from "@/lib/scheduler/types";

interface SchedulerDebugHeaderProps {
  configJson: Record<string, unknown>;
  sourcesEnabled: string[];
}

export function SchedulerDebugHeader({ configJson, sourcesEnabled }: SchedulerDebugHeaderProps) {
  const { toast } = useToast();
  
  // Only show for scheduler-created runs
  const isSchedulerRun = configJson?.from_schedule === true;
  if (!isSchedulerRun) return null;

  const scheduleRunId = configJson?.schedule_run_id as string | undefined;
  const scheduleId = configJson?.schedule_id as string | undefined;
  const approvalCheckpoint = configJson?.approval_checkpoint as ApprovalCheckpoint | undefined;
  
  // Determine source type from sources_enabled array
  const sourceType = sourcesEnabled.length === 1 
    ? sourcesEnabled[0] 
    : sourcesEnabled.length > 1 
      ? sourcesEnabled.join(" + ") 
      : undefined;

  // Get run until config for display
  const runUntilConfig = getRunUntilConfig(approvalCheckpoint);

  // Check for missing/unexpected values
  const hasMissingValues = !sourceType || !scheduleRunId;

  const handleCopyScheduleRunId = () => {
    if (scheduleRunId) {
      navigator.clipboard.writeText(scheduleRunId);
      toast({
        title: "Copied",
        description: "Schedule run ID copied to clipboard",
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-3 px-3 py-2 bg-muted/50 rounded-md border border-border/50 text-sm">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground font-medium">Scheduler Debug</span>
        </div>
        
        <div className="h-4 w-px bg-border" />
        
        {/* Source */}
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Source:</span>
          {sourceType ? (
            <Badge variant="outline" className="font-mono text-xs">
              {sourceType}
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-xs gap-1">
              <AlertTriangle className="h-3 w-3" />
              Unknown
            </Badge>
          )}
        </div>

        <div className="h-4 w-px bg-border" />

        {/* Schedule Run ID */}
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Schedule Run:</span>
          {scheduleRunId ? (
            <div className="flex items-center gap-1">
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                {scheduleRunId.slice(0, 8)}
              </code>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={handleCopyScheduleRunId}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy full ID</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <Badge variant="destructive" className="text-xs gap-1">
              <AlertTriangle className="h-3 w-3" />
              Unknown
            </Badge>
          )}
        </div>

        <div className="h-4 w-px bg-border" />

        {/* Run Until */}
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Run until:</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge 
                variant={approvalCheckpoint ? "secondary" : "outline"}
                className="text-xs cursor-help"
              >
                {runUntilConfig.label}
                <span className="ml-1 text-muted-foreground">({runUntilConfig.steps})</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-medium">{runUntilConfig.tooltipTitle}</p>
              <p className="text-muted-foreground">{runUntilConfig.tooltipBody}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Warning for missing values */}
        {hasMissingValues && (
          <>
            <div className="h-4 w-px bg-border" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="destructive" className="text-xs gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Missing Data
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                Some scheduler metadata is missing. This may indicate a dispatch bug.
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
